import moment from 'moment';
import { useQuery } from 'react-query';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import { CareType, getCareTeam, ProviderWithCareTypes } from '~/core/dapi/careTeam';
import useSelectActiveUserProfile from '~/hooks/useSelectActiveUserProfile';
import { usePreloadSlots } from './useSlots';

export type LocationWithCareTypes = LocationInterface & {
  careTypes: CareType[];
};

// This type will handle both snake_case and camelCase keys for the provider and location interface
type WithLastBooked = {
  lastBooked?: string;
  last_booked?: string;
};

/**
 * Comparator to sort by a lastBooked date
 *
 * @returns number
 */
export function sortByLastBooked(a: WithLastBooked, b: WithLastBooked) {
  const lastBookedA = a.lastBooked ?? a.last_booked;
  const lastBookedB = b.lastBooked ?? b.last_booked;
  if (lastBookedA && lastBookedB) {
    return moment(lastBookedB).diff(moment(lastBookedA));
  }
  if (lastBookedA) {
    return -1;
  }
  return 1;
}

/**
 * Get the current user profile's care team
 *
 * @returns the useQuery value
 */
export function useCareTeam({ preloadSlots = false }: { preloadSlots?: boolean } = {}) {
  const userProfile = useSelectActiveUserProfile();

  const query = useQuery(
    ['care-team', userProfile?.user_profile_id],
    () => getCareTeam(userProfile?.user_profile_id),
    { staleTime: 10000 }
  );

  const covidLocations = query?.data?.data.covid.map((loc) => ({
    ...loc,
  })) as LocationWithCareTypes[];

  const videoLocations = query?.data?.data.video.map((loc) => ({
    ...loc,
  })) as LocationWithCareTypes[];

  const ucLocations = query?.data?.data.uc.map((loc) => ({
    ...loc,
  })) as LocationWithCareTypes[];

  const providerBookings = query?.data?.data.provider.map((provider) => ({
    ...provider,
  })) as ProviderWithCareTypes[];

  /**
   * This is an imperfect method of aggregating the returned locations
   * so that we can list whether a given location also supports in-person
   * or telemed, respectively.
   *
   * Ideally, this would be returned from the database, but for now, it
   * should suffice.
   */
  const rawAllLocations = query.data?.data
    ? [...covidLocations, ...videoLocations, ...ucLocations]
    : [];

  const allLocations: LocationWithCareTypes[] = Object.values(
    rawAllLocations.reduce(
      // Merge locations into a unique list
      (acc, loc) => {
        const existingLoc = acc[loc.id];
        const careTypes: CareType[] = existingLoc?.careTypes ?? [];

        if (loc.isTelemed) {
          // Telemed locations don't link to flexible CDPs so we don't
          // list urgent care even if they have an associated location that supports it.
          //
          // Eventually, it would be nice to streamline this so that
          // we can merge telemed/in-person locations here, but
          // that creates a lot of complexity on the frontend that
          // would be harder to maintain than it's worth.
          careTypes.push('video');
        } else {
          careTypes.push('uc');

          if (
            loc.associatedTelemedLocationId &&
            !!rawAllLocations.find((otherLoc) => otherLoc.id === loc.associatedTelemedLocationId)
          ) {
            careTypes.push('video');
          }
        }

        return {
          ...acc,
          // Merge their `__careType` fields into a list of supported types
          [loc.id]: { ...loc, careTypes: [...new Set(careTypes)] },
        };
      },
      {} as Record<string, LocationWithCareTypes>
    )
  ).sort(sortByLastBooked);

  const rawAllProviders = providerBookings ?? [];

  const allProviders: ProviderWithCareTypes[] = Object.values(
    rawAllProviders.reduce((acc, provider) => {
      const existingProvider = acc[provider.id];
      const careTypes: CareType[] = existingProvider?.careTypes ?? [];
      careTypes.push('provider');

      return {
        ...acc,
        [provider.id]: { ...provider, careTypes: [...new Set(careTypes)] },
      };
    }, {} as Record<string, ProviderWithCareTypes>)
  );

  const allCareTypes = [...allLocations, ...allProviders].sort(sortByLastBooked);

  usePreloadSlots(
    allLocations.map((l) => l.id),
    { enabled: preloadSlots }
  );

  return { ...query, allLocations, allCareTypes };
}
